import React from 'react'

/**
 * 일정관리·대시보드 공용: 접이식「표시·필터」블록.
 * 상태·뮤테이션은 부모에서 관리하고 UI만 공유한다.
 */
export default function CalendarFiltersPanel({
    panelIdPrefix = 'calendar',
    filtersExpanded,
    onFiltersExpandedChange,
    showInsDigest,
    showBirthDigest,
    showAnnivDigest,
    groupCustomerAutoEvents,
    onGroupCustomerAutoEventsChange,
    digestPrefsMutation,
    includeCancelled,
    onIncludeCancelledChange,
    showCustomerContactEvents,
    onShowCustomerContactEventsChange,
    showMaturityEvents,
    onShowMaturityEventsChange,
    showClaimEvents,
    onShowClaimEventsChange,
    hostOrgOptions = [],
    hiddenHostOrgIds,
    onToggleHiddenHost,
    canUseTeamCalendarView,
    teamCalendarOrgChoicesResolved = [],
    teamVisibleOrgIds,
    onToggleTeamVisibleOrg,
}) {
    const toggleId = `${panelIdPrefix}-filters-toggle`
    const panelId = `${panelIdPrefix}-filters-panel`

    return (
        <div className="rounded border border-slate-200 bg-slate-50/90 text-sm overflow-hidden">
            <button
                type="button"
                className="flex w-full items-center justify-between gap-2 px-2.5 py-2 text-left hover:bg-slate-100/70 transition-colors"
                aria-expanded={filtersExpanded}
                aria-controls={panelId}
                id={toggleId}
                onClick={() => onFiltersExpandedChange(!filtersExpanded)}
            >
                <span className="font-medium text-slate-800 text-sm">표시·필터</span>
                <span className="flex items-center gap-1 text-xs text-slate-500 shrink-0">
                    {filtersExpanded ? '접기' : '펼치기'}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className={`h-4 w-4 text-slate-500 transition-transform duration-200 ${filtersExpanded ? 'rotate-180' : ''}`}
                        aria-hidden
                    >
                        <path
                            fillRule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                            clipRule="evenodd"
                        />
                    </svg>
                </span>
            </button>
            {filtersExpanded && (
                <div id={panelId} className="border-t border-slate-200/80 px-2.5 pb-2.5 pt-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
                        <div className="rounded border border-white/80 bg-white/60 px-2 py-1.5 space-y-1">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-semibold text-slate-700">자동 일정</span>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={groupCustomerAutoEvents}
                                    style={{ fontSize: '11px', lineHeight: 1.1 }}
                                    onClick={() => onGroupCustomerAutoEventsChange(!groupCustomerAutoEvents)}
                                    className={[
                                        'inline-flex h-5 shrink-0 items-center gap-0.5 rounded-full border px-1 text-[11px] font-normal transition-colors',
                                        groupCustomerAutoEvents
                                            ? 'border-slate-800 bg-slate-800 text-white'
                                            : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50',
                                    ].join(' ')}
                                >
                                    <span style={{ fontSize: '11px' }}>이벤트 모아보기</span>
                                    <span
                                        className={groupCustomerAutoEvents ? 'rounded-full bg-white/20 px-1 text-[11px]' : 'rounded-full bg-slate-100 px-1 text-[11px] text-slate-500'}
                                        style={{ fontSize: '11px' }}
                                    >
                                        {groupCustomerAutoEvents ? 'ON' : 'OFF'}
                                    </span>
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                                <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={showInsDigest}
                                        disabled={digestPrefsMutation.isPending}
                                        onChange={(e) => digestPrefsMutation.mutate({
                                            calendarShowInsuranceAgeDigest: e.target.checked,
                                        })}
                                    />
                                    상령일
                                </label>
                                <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={showBirthDigest}
                                        disabled={digestPrefsMutation.isPending}
                                        onChange={(e) => digestPrefsMutation.mutate({
                                            calendarShowBirthdayDigest: e.target.checked,
                                        })}
                                    />
                                    생일
                                </label>
                                <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={showAnnivDigest}
                                        disabled={digestPrefsMutation.isPending}
                                        onChange={(e) => digestPrefsMutation.mutate({
                                            calendarShowAnniversaryDigest: e.target.checked,
                                        })}
                                    />
                                    기념일
                                </label>
                            </div>
                        </div>
                        <div className="rounded border border-white/80 bg-white/60 px-2 py-1.5 space-y-1">
                            <div className="flex items-center gap-1">
                                <span className="text-xs font-semibold text-slate-700">일정 목록</span>
                            </div>
                            <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-700">
                                <input
                                    type="checkbox"
                                    checked={includeCancelled}
                                    onChange={(e) => onIncludeCancelledChange(e.target.checked)}
                                />
                                취소된 일정 포함
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-700">
                                <input
                                    type="checkbox"
                                    checked={showCustomerContactEvents}
                                    onChange={(e) => onShowCustomerContactEventsChange(e.target.checked)}
                                />
                                고객 연락 일정
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-700">
                                <input
                                    type="checkbox"
                                    checked={showMaturityEvents}
                                    onChange={(e) => onShowMaturityEventsChange(e.target.checked)}
                                />
                                만기계약
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-700">
                                <input
                                    type="checkbox"
                                    checked={showClaimEvents}
                                    onChange={(e) => onShowClaimEventsChange(e.target.checked)}
                                />
                                보험금 청구
                            </label>
                        </div>
                        {hostOrgOptions.length > 0 && (
                            <div className="rounded border border-white/80 bg-white/60 px-2 py-1.5 space-y-1 sm:col-span-2 xl:col-span-1">
                                <div className="flex items-center gap-1">
                                    <span className="text-xs font-semibold text-slate-700">통합 보기 — 기준 조직 숨김</span>
                                </div>
                                <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                                    {hostOrgOptions.map((o) => (
                                        <label key={o.id} className="flex items-center gap-1 rounded border border-slate-200/80 bg-white px-1.5 py-0.5 text-[11px]">
                                            <input
                                                type="checkbox"
                                                checked={hiddenHostOrgIds.has(o.id)}
                                                onChange={() => onToggleHiddenHost(o.id)}
                                            />
                                            <span className="truncate max-w-[8rem]">{o.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                        {canUseTeamCalendarView && teamCalendarOrgChoicesResolved.length > 0 && (
                            <div className="rounded border border-indigo-100 bg-indigo-50/50 px-2 py-1.5 space-y-1 sm:col-span-2 xl:col-span-1">
                                <div className="flex items-center gap-1">
                                    <span className="text-xs font-semibold text-indigo-900">조직원 일정</span>
                                </div>
                                <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                                    {teamCalendarOrgChoicesResolved.map((o) => {
                                        const id = Number(o.id)
                                        return (
                                            <label key={id} className="flex items-center gap-1 rounded border border-indigo-200/60 bg-white px-1.5 py-0.5 text-[11px]">
                                                <input
                                                    type="checkbox"
                                                    checked={teamVisibleOrgIds.has(id)}
                                                    onChange={() => onToggleTeamVisibleOrg(id)}
                                                />
                                                <span className="truncate max-w-[8rem]">{o.name || o.orgName || `조직 #${id}`}</span>
                                            </label>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
